import React, { useState, useEffect, useRef } from "react";

import LazyLoadVideo from "@/common/LazyLoadVideo";

import { AlertCardImageContainer } from "./AlertCardImageContainer";
import styles from "./ImageGallery.module.scss";

interface ILoadedImage {
  width: number;
  height: number;
  ratio: number;
  url: string;
}
interface ILoadedVideo {
  url: string;
  type: "video";
}

interface AlertCardMediaProps {
  mediaDetails: { media_type: "video" | "image"; download_url: string }[];
  resolved: boolean;
}

const AlertCardMedia: React.FC<AlertCardMediaProps> = ({
  mediaDetails,
  resolved = false,
}) => {
  const resolvedMediaClass = resolved ? styles.resolvedMedia : "";

  const [loadedMedia, setLoadedMedia] = useState<
    (ILoadedImage | ILoadedVideo)[]
  >([]);
  const [isInView, setIsInView] = useState(false);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 260,
    height: 260,
  });

  const hasVideo = mediaDetails.some((media) => media.media_type === "video");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
          }
        });
      },
      { threshold: 0.1 },
    );

    if (mediaContainerRef.current) {
      observer.observe(mediaContainerRef.current);
    }

    return () => {
      if (mediaContainerRef.current) {
        observer.unobserve(mediaContainerRef.current);
      }
    };
  }, []);

  // Listen for container resize
  useEffect(() => {
    if (!mediaContainerRef.current) return;

    // Update dimensions initially
    setContainerDimensions({
      width: mediaContainerRef.current.clientWidth || 260,
      height: mediaContainerRef.current.clientHeight || 260,
    });

    // Create ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === mediaContainerRef.current) {
          setContainerDimensions({
            width: entry.contentRect.width || 260,
            height: entry.contentRect.height || 260,
          });
        }
      }
    });

    resizeObserver.observe(mediaContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mediaContainerRef.current]);

  useEffect(() => {
    if (isInView) {
      const tmpLoadedMedia: (ILoadedImage | ILoadedVideo)[] = [];

      if (!hasVideo) {
        mediaDetails
          .filter((media) => media.media_type === "image")
          .forEach(({ download_url: url }) => {
            const img = new Image();
            img.onload = () => {
              tmpLoadedMedia.push({
                width: img.naturalWidth,
                height: img.naturalHeight,
                ratio: img.naturalWidth / img.naturalHeight,
                url,
              });
              setLoadedMedia([...tmpLoadedMedia]);
            };
            img.src = url;
          });
      } else {
        const firstVideoUrl = mediaDetails.find(
          (media) => media.media_type === "video",
        )?.download_url;
        if (firstVideoUrl) {
          tmpLoadedMedia.push({ url: firstVideoUrl, type: "video" });
          setLoadedMedia([...tmpLoadedMedia]);
        }
      }
    }
  }, [isInView, mediaDetails, hasVideo]);

  return (
    <div
      className="image_gallery w-100 h-90"
      ref={mediaContainerRef}
      // h-90 (height: 90%) cannot resolve against the parent's aspect-ratio
      // derived height, so the gallery would collapse to 0px before any image
      // loads. That left the IntersectionObserver below its threshold forever,
      // so isInView never flipped and no image requests were ever made.
      // A reserved min-height gives the observer a real target to intersect.
      style={{ minHeight: 250 }}
    >
      {hasVideo ? (
        <div className={styles.videoContainer}>
          {loadedMedia.map((media) => {
            if ("type" in media && media.type === "video") {
              return (
                <LazyLoadVideo
                  key={media.url}
                  src={media.url}
                  muted
                  controls={false}
                  className={`${resolvedMediaClass}`}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                />
              );
            }
            return null;
          })}
        </div>
      ) : (
        <div className={styles.imageContainer}>
          {loadedMedia.length > 0 && (
            <AlertCardImageContainer
              images={loadedMedia.map((media) =>
                "url" in media ? media.url : "",
              )}
              containerWidth={containerDimensions.width}
              containerHeight={containerDimensions.height}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AlertCardMedia;
